<template>
  <div class="animated fadeIn">
    <b-row class="mb-4">
      <b-col xl="12" lg="12">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header class="d-flex justify-content-between align-items-center">
            <div>
              <i class="fa fa-filter text-warning"></i> <strong>Filters</strong>
            </div>
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="4">
                <b-form-group label="Range" label-class="font-weight-bold small text-muted text-uppercase">
                  <b-form-radio-group
                    v-model="activeRange"
                    @change="onRangeChange"
                    :options="rangeOptions"
                    buttons
                    button-variant="outline-secondary"
                    size="sm"
                    name="range-filter"
                    class="w-100 dark-radio-group"
                  ></b-form-radio-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Server" label-class="font-weight-bold small text-muted text-uppercase">
                  <b-form-radio-group
                    v-model="serverFilter"
                    @change="activeExternalFilter"
                    :options="serverOptions"
                    buttons
                    button-variant="outline-secondary"
                    size="sm"
                    name="server-filter"
                    class="w-100 dark-radio-group"
                  ></b-form-radio-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Status" label-class="font-weight-bold small text-muted text-uppercase">
                  <b-form-radio-group
                    v-model="statusFilter"
                    @change="activeExternalFilter"
                    :options="statusOptions"
                    buttons
                    button-variant="outline-secondary"
                    size="sm"
                    name="status-filter"
                    class="w-100 dark-radio-group"
                  ></b-form-radio-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-card no-body class="shadow-sm">
          <b-card-header class="d-flex flex-wrap justify-content-between align-items-center py-2">
            <div class="mr-3">
              <i class="fa fa-clock-o text-info"></i> <strong>{{ activeRangeLabel }} Session Time</strong>
              <span v-if="loading" class="session-loading-indicator ml-2">
                <i class="fa fa-circle-o-notch fa-spin mr-1"></i> Loading...
              </span>
              <div class="small text-muted">Time gained between the last two playtime snapshots. Sorted by session time (descending).</div>
              <div class="small text-muted">
                Snapshot: {{ lastSnapshotLabel }}
                <span v-if="isSnapshotStale" class="text-warning ml-2">
                  <i class="fa fa-exclamation-triangle mr-1"></i> outdated
                </span>
              </div>
              <div v-if="hasOddSessions" class="small text-warning">
                <i class="fa fa-exclamation-triangle mr-1"></i> Some sessions exceed 24h (likely transfers/anomalies).
              </div>
            </div>

            <div class="d-flex flex-wrap align-items-center">
              <div class="stat-pill mr-2 mb-2 mb-md-0">
                <span class="label">Entries</span>
                <span class="value">{{ totalCount }}</span>
              </div>
              <div class="stat-pill mr-2 mb-2 mb-md-0">
                <span class="label">Online</span>
                <span class="value text-success">{{ onlineCount }}</span>
              </div>
              <div class="stat-pill mr-3 mb-2 mb-md-0">
                <span class="label">Offline</span>
                <span class="value text-muted">{{ offlineCount }}</span>
              </div>
              <b-input-group size="sm" class="grid-search-group mr-2">
                <b-input-group-prepend is-text class="bg-dark border-secondary"><i class="fa fa-search small"></i></b-input-group-prepend>
                <b-form-input
                  v-model="quickFilterText"
                  placeholder="Quick search..."
                  class="bg-dark text-white border-secondary"
                  @input="onQuickFilterChanged"
                ></b-form-input>
                <b-input-group-append v-if="quickFilterText">
                  <b-button variant="outline-secondary" class="border-secondary py-0" @click="quickFilterText = ''; onQuickFilterChanged()">
                    <i class="fa fa-times small"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </b-card-header>

          <div class="table-wrapper position-relative">
            <ag-grid-vue class="ag-theme-balham-dark" style="width: 100%; height: 65vh"
                         :gridOptions='gridOptions'
                         :isExternalFilterPresent="isExternalFilterPresent"
                         :doesExternalFilterPass="doesExternalFilterPass"
                         :rowData="playtimeToplist">
            </ag-grid-vue>
            <div v-if="loading" class="table-loading d-flex align-items-center justify-content-center">
              <div class="text-center">
                <b-spinner small variant="primary"></b-spinner>
                <div class="small text-muted mt-2">Loading session time...</div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { AgGridVue } from 'ag-grid-vue'
import { DefaultGridOptions } from "@/default_table_settings"
import { applyGridPersistence } from "@/grid_persistence"
import Servers from '../../_servers'
import moment from 'moment'

export default {
  name: 'Playtime',
  components: {
    'ag-grid-vue': AgGridVue
  },
  computed: {
    ...mapGetters({
      playtimeToplistByRange: 'playtimeToplistByRange',
      playtimeUpdatedAtByRange: 'playtimeUpdatedAtByRange'
    }),
    activeRangeLabel() {
      const match = this.rangeOptions.find(opt => opt.value === this.activeRange)
      return match ? match.text : 'Day'
    },
    playtimeToplist() {
      return this.playtimeToplistByRange[this.activeRange] || []
    },
    playtimeUpdatedAt() {
      return this.playtimeUpdatedAtByRange[this.activeRange]
    },
    totalCount() {
      return this.playtimeToplist ? this.playtimeToplist.length : 0
    },
    onlineCount() {
      return this.playtimeToplist.filter(p => p.online).length
    },
    offlineCount() {
      return this.playtimeToplist.filter(p => !p.online).length
    },
    lastUpdated() {
      if (!this.playtimeUpdatedAt) return 'n/a'
      return moment(this.playtimeUpdatedAt).format('YYYY-MM-DD HH:mm:ss')
    },
    lastSnapshotLabel() {
      if (!this.lastSnapshot) return 'n/a'
      return moment(this.lastSnapshot).format('YYYY-MM-DD HH:mm:ss')
    },
    lastSnapshot() {
      if (!this.playtimeToplist || this.playtimeToplist.length === 0) return null
      return this.playtimeToplist[0].last_snapshot || null
    },
    isSnapshotStale() {
      if (!this.lastSnapshot) return false
      return moment().diff(moment(this.lastSnapshot), 'hours') > 36
    },
    hasOddSessions() {
      if (this.activeRange !== 'day') return false
      return this.playtimeToplist.some(p => (p.yesterday_minutes || 0) > 24 * 60)
    }
  },
  data() {
    return {
      loading: true,
      lastUpdatedAt: null,
      quickFilterText: '',
      serverFilter: 'All',
      statusFilter: 'All',
      activeRange: 'day',
      gridOptions: null,
      rangeOptions: [
        { text: 'Day', value: 'day' },
        { text: 'Week', value: 'week' },
        { text: 'Month', value: 'month' }
      ],
      serverOptions: [
        { text: 'All', value: 'All' },
        { text: 'Gold', value: 'Gold' },
        { text: 'Silver', value: 'Silver' }
      ],
      statusOptions: [
        { text: 'All', value: 'All' },
        { text: 'Online', value: 'Online' },
        { text: 'Offline', value: 'Offline' }
      ]
    }
  },
  methods: {
    onQuickFilterChanged() {
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.setQuickFilter(this.quickFilterText)
      }
    },
    refreshPlaytime() {
      this.loading = true
      this.lastUpdatedAt = this.playtimeUpdatedAt
      this.emit('getPlaytimeToplist', { range: this.activeRange })
    },
    onRangeChange() {
      this.loading = true
      this.refreshPlaytime()
      this.updateRangeHeaders()
    },
    doesExternalFilterPass(node) {
      if (!node || !node.data) return true

      const server = (node.data.server || '').toLowerCase()
      if (this.serverFilter === 'Gold' && server !== 'gold') return false
      if (this.serverFilter === 'Silver' && server !== 'silver') return false

      if (this.statusFilter === 'Online' && !node.data.online) return false
      if (this.statusFilter === 'Offline' && node.data.online) return false

      return true
    },
    isExternalFilterPresent() {
      return this.serverFilter !== 'All' || this.statusFilter !== 'All'
    },
    activeExternalFilter() {
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.onFilterChanged()
      }
    },
    formatMinutes(totalMinutes) {
      const minutes = Number(totalMinutes) || 0
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return `${h}h ${m}m`
    },
    formatTotalPlaytime(totalMinutes) {
      if (!totalMinutes) return '-'
      return this.formatMinutes(totalMinutes)
    },
    averageMinutes(totalMinutes) {
      const days = this.activeRange === 'week' ? 7 : (this.activeRange === 'month' ? 30 : 1)
      if (!totalMinutes) return 0
      return Math.round(Number(totalMinutes) / days)
    },
    createColumnDefs() {
      const self = this
      return [
        {
          headerName: 'Username',
          field: 'username',
          filter: 'agTextColumnFilter',
          width: 200,
          cellRenderer: (params) => {
            if (!params.value) return ''
            const route = "/user/" + params.value + "/profile"
            const link = document.createElement("a")
            link.href = self.$router.resolve(route).href
            link.innerText = params.value
            link.className = "font-weight-bold text-primary"
            link.addEventListener("click", e => {
              e.preventDefault()
              self.$router.push(route)
            })
            return link
          }
        },
        {
          headerName: 'Server',
          field: 'server',
          width: 110,
          cellRenderer: (params) => {
            const server = (params.value || '').toLowerCase()
            const color = (Servers[server] && Servers[server].colour) ? Servers[server].colour : 'secondary'
            const label = (params.value || '').toUpperCase()
            return `<span class="badge badge-${color} text-uppercase px-2">${label}</span>`
          }
        },
        {
          headerName: `${self.activeRangeLabel} Session Time`,
          field: 'yesterday_minutes',
          width: 170,
          sortable: true,
          sort: 'desc',
          sortIndex: 0,
          valueGetter: params => params.data ? params.data.yesterday_minutes : 0,
          cellRenderer: params => `<span class="font-weight-bold">${self.formatMinutes(params.value)}</span>`
        },
        {
          headerName: 'Daily Average',
          field: 'daily_average',
          width: 140,
          sortable: true,
          hide: self.activeRange === 'day',
          valueGetter: params => params.data ? self.averageMinutes(params.data.yesterday_minutes) : 0,
          cellRenderer: params => `<span class="text-muted">${self.formatMinutes(params.value)}</span>`
        },
        {
          headerName: 'Total Playtime',
          field: 'playtime',
          width: 140,
          cellRenderer: params => `<span class="text-muted">${self.formatTotalPlaytime(params.value)}</span>`
        },
        {
          headerName: 'Status',
          field: 'online',
          width: 110,
          sortable: true,
          cellRenderer: params => {
            if (params.value) {
              return '<span class="badge badge-success px-2">Online</span>'
            }
            return '<span class="badge badge-secondary px-2">Offline</span>'
          }
        }
      ]
    },
    updateRangeHeaders() {
      if (!this.gridOptions || !this.gridOptions.api) return
      const defs = this.gridOptions.columnDefs || []
      const sessionCol = defs.find(col => col.field === 'yesterday_minutes')
      if (sessionCol) {
        sessionCol.headerName = `${this.activeRangeLabel} Session Time`
      }
      const avgCol = defs.find(col => col.field === 'daily_average')
      if (avgCol) {
        avgCol.hide = this.activeRange === 'day'
      }
      this.gridOptions.api.setColumnDefs(defs)
      this.$nextTick(() => {
        if (this.gridOptions && this.gridOptions.api) {
          this.gridOptions.api.sizeColumnsToFit()
        }
      })
    }
  },
  beforeMount() {
    this.gridOptions = Object.assign({}, DefaultGridOptions, {
      rowSelection: 'single',
      suppressRowClickSelection: true,
      columnDefs: this.createColumnDefs(),
      onGridReady: () => {}
    })
    applyGridPersistence(this.gridOptions, 'playtime-grid')
  },
  mounted() {
    this.loading = true
    this.refreshPlaytime()
  },
  watch: {
    playtimeUpdatedAt() {
      if (this.loading && this.playtimeUpdatedAt !== this.lastUpdatedAt) {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, rgba(32, 168, 216, 0.12), rgba(32, 168, 216, 0.02));
  border: 1px solid rgba(32, 168, 216, 0.2);
}
.stat-pill {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 6px 12px;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.stat-pill .label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #a4b7c1;
  font-weight: 700;
}
.stat-pill .value {
  font-size: 1rem;
  font-weight: 700;
}
.table-wrapper {
  min-height: 65vh;
}
.table-loading {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(1px);
  z-index: 2;
}
.grid-search-group {
  width: 220px;
}
.session-loading-indicator {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: #8fd6cc;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.dark-radio-group .btn-outline-secondary {
  border-color: #4f5d73;
  color: #a4b7c1;
}
.dark-radio-group .btn-outline-secondary.active {
  background-color: #20a8d8 !important;
  border-color: #20a8d8 !important;
}
/deep/ .ag-theme-balham-dark .ag-header {
  background: rgba(20, 24, 28, 0.9);
}
/deep/ .ag-theme-balham-dark .ag-header-cell-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9fb0ba;
}
/deep/ .ag-theme-balham-dark .ag-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
/deep/ .ag-theme-balham-dark .ag-row-odd {
  background-color: rgba(0, 0, 0, 0.12);
}
/deep/ .ag-theme-balham-dark .ag-row-hover {
  background-color: rgba(32, 168, 216, 0.08) !important;
}
/deep/ .ag-theme-balham-dark .ag-cell {
  font-size: 0.8rem;
  color: #e5edf2;
}
</style>
